import type { Chamada } from '@plane/shared';

import { TituloComDestaque } from '@/components/titulo-com-destaque';
import { useHero } from '@/hooks/use-conteudo';
import { FundoDoHero } from '@/modules/home/components/fundo-do-hero';

/**
 * Abertura do site.
 *
 * O conteúdo vem do painel, inclusive a escolha do fundo — verde sólido,
 * imagem ou vídeo do YouTube. Ver `fundo-do-hero.tsx`, onde estão as ressalvas
 * de autoplay e privacidade.
 */
export function HeroSection() {
  const hero = useHero();

  return (
    <section
      id="topo"
      className="relative isolate flex min-h-[clamp(560px,88vh,860px)] flex-col justify-end overflow-hidden bg-brand-950 text-bone"
    >
      <FundoDoHero fundo={hero.fundo} />

      {/* Véu do protótipo. Sobre o vídeo também: é o que segura a legibilidade
          do título quando o quadro do fundo clareia. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-t from-brand-950 via-brand-950/70 to-brand-900/80"
      />

      <div className="mx-auto w-full max-w-[1360px] px-6 py-[clamp(48px,8vw,96px)]">
        <p className="flex items-center gap-2.5 text-[0.72rem] tracking-[0.22em] text-brass-light uppercase">
          <span aria-hidden="true" className="inline-block h-px w-8 bg-brass-light/75" />
          {hero.kicker}
        </p>

        <TituloComDestaque
          como="h1"
          texto={hero.titulo}
          className="mt-6 max-w-[18ch] font-display text-[clamp(2.4rem,7vw,5rem)] leading-[1.02] text-balance"
          destaqueClassName="text-brass italic"
          vazadoClassName="texto-vazado"
        />

        <div className="mt-10 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <p className="max-w-[52ch] text-sm leading-relaxed text-brand-150">{hero.descricao}</p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <ChamadaPrincipal chamada={hero.chamadaPrincipal} />

            <a
              href={hero.chamadaSecundaria.ancora}
              className="text-sm text-bone underline underline-offset-4 transition-colors hover:text-brass-light"
            >
              {hero.chamadaSecundaria.rotulo}
            </a>
          </div>
        </div>
      </div>

      {/*
        Legível por leitor de tela, ao contrário do protótipo.

        Lá a faixa estava em `aria-hidden`, provavelmente porque visualmente ela
        parece enfeite. Mas o conteúdo é argumento de venda — tempo de casa,
        volume entregue e garantia —, e é justamente o que alguém avaliando a
        marcenaria precisa saber. Só os pontos separadores continuam decorativos.
      */}
      {hero.numeros.length > 0 && (
        <aside aria-label="A Plane em números" className="border-t border-bone/15 bg-brand-950/60">
          <ul className="mx-auto flex max-w-[1360px] flex-wrap items-center gap-x-4 gap-y-2 px-6 py-4 text-[0.64rem] tracking-[0.18em] text-brand-150 uppercase">
            {hero.numeros.map((numero, indice) => (
              <li key={numero} className="flex items-center gap-4">
                {indice > 0 && (
                  <span aria-hidden="true" className="inline-block h-1 w-1 rounded-full bg-brass" />
                )}
                {numero}
              </li>
            ))}
          </ul>
        </aside>
      )}
    </section>
  );
}

function ChamadaPrincipal({ chamada }: { chamada: Chamada }) {
  return (
    <a
      href={chamada.ancora}
      className="inline-flex items-center gap-2 bg-brass px-6 py-3.5 text-[0.72rem] tracking-[0.14em] text-brand-950 uppercase transition-colors hover:bg-bone"
    >
      {chamada.rotulo}
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
        <path
          d="M4 12h15m-6-6 6 6-6 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
}
